// src/pages/admin/AdminDashboard.tsx
import { useState, useCallback, useEffect } from 'react';
import {
  Box, Container, Alert, Typography, IconButton, Snackbar,
  Grid,
} from '@mui/material';
import { Notifications as NotificationsIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApplications } from '../admin/hooks/useApplications';
import { useDashboardStats } from './hooks/useDashboardStats';
import { useInquiries } from './hooks/useInquiries';
import { useDashboardAuxData } from './hooks/useDashboardAuxData';

import DashboardHeader from './components/dashboard/DashboardHeader';
import DashboardSkeleton from './components/dashboard/DashboardSkeleton';
import KPICardRow from './components/dashboard/KPICardRow';
import RevenueAreaChart from './components/dashboard/RevenueAreaChart';
import StatusDistributionChart from './components/dashboard/StatusDistributionChart';
import DailyTrendChart from './components/dashboard/DailyTrendChart';
import ChannelBreakdownChart from './components/dashboard/ChannelBreakdownChart';
import StatusSummaryGrid from './components/dashboard/StatusSummaryGrid';
import RecentApplicationsTable from './components/dashboard/RecentApplicationsTable';
import PendingInquiriesWidget from './components/dashboard/PendingInquiriesWidget';
import QuickStatsBar from './components/dashboard/QuickStatsBar';
import QuickActionFABs from './components/dashboard/QuickActionFABs';

const AdminDashboard = () => {
  const { applications, loading, error, refetch } = useApplications();
  const stats = useDashboardStats(applications);
  const { inquiries, pendingCount, loading: inqLoading } = useInquiries();
  const { activeNoticesCount, activeFaqsCount } = useDashboardAuxData();
  const navigate = useNavigate();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastCount, setLastCount] = useState(0);
  const [showNewAlert, setShowNewAlert] = useState(false);
  const [newCount, setNewCount] = useState(0);

  // 새 신청 감지
  useEffect(() => {
    if (applications.length > 0) {
      if (lastCount > 0 && applications.length > lastCount) {
        setNewCount(applications.length - lastCount);
        setShowNewAlert(true);
        setTimeout(() => setShowNewAlert(false), 5000);
      }
      setLastCount(applications.length);
    }
  }, [applications.length, lastCount]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try { await refetch?.(); }
    catch { /* noop */ }
    finally { setIsRefreshing(false); }
  }, [refetch]);

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 5, textAlign: 'center' }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 2, mb: 3 }}
          action={
            <IconButton color="inherit" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          }
        >
          <Typography variant="h6" gutterBottom>데이터를 불러오는 중 오류가 발생했습니다</Typography>
          <Typography variant="body2">네트워크 연결을 확인하고 새로고침 버튼을 클릭해주세요.</Typography>
        </Alert>
      </Container>
    );
  }

  if (loading) return <DashboardSkeleton />;

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', position: 'relative' }} role="main" aria-label="관리자 대시보드">
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>

        {/* 실시간 알림 */}
        {showNewAlert && newCount > 0 && (
          <Alert icon={<NotificationsIcon />} severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {newCount}개의 새로운 신청이 접수되었습니다!
          </Alert>
        )}

        {/* 헤더 */}
        <DashboardHeader todayCount={stats.todayCount} isRefreshing={isRefreshing} onRefresh={handleRefresh} />

        {/* KPI 카드 */}
        <KPICardRow stats={stats} onNavigate={navigate} />

        {/* 상태별 현황 */}
        <StatusSummaryGrid applications={applications} />

        {/* 차트 Row 1: 매출 + 상태 분포 */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <RevenueAreaChart data={stats.monthlyRevenue} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatusDistributionChart data={stats.statusDistribution} total={stats.total} />
          </Grid>
        </Grid>

        {/* 차트 Row 2: 일별 트렌드 + 채널 */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <DailyTrendChart data={stats.dailyTrend} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChannelBreakdownChart data={stats.channelBreakdown} />
          </Grid>
        </Grid>

        {/* 최근 신청 + 미처리 문의 */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <RecentApplicationsTable applications={applications} />
          </Grid>
          <Grid item xs={12} md={4}>
            <PendingInquiriesWidget inquiries={inquiries} loading={inqLoading} />
          </Grid>
        </Grid>

        {/* 빠른 통계 바 */}
        <QuickStatsBar noticesCount={activeNoticesCount} faqsCount={activeFaqsCount} totalDevices={stats.totalDevices} />

      </Container>

      <QuickActionFABs />

      <Snackbar
        open={isRefreshing}
        message="데이터를 새로고침하고 있습니다..."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />

      <style>{`
        @keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .rotating { animation: rotate 1s linear infinite; }
      `}</style>
    </Box>
  );
};

export default AdminDashboard;
